import React, { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, X, ArrowSquareOut } from "@phosphor-icons/react";

export const documentUrl = (id) => `${API}/inventory/documents/${id}/download`;

/**
 * Uploads a file to /inventory/documents as soon as it is picked and reports
 * the stored document ({ id, file_name, ... }) via onChange. `value` is that
 * document, or null when nothing is attached.
 */
export default function DocumentUpload({ kind, value, onChange, label = "Attach file", testId }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (file) => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    try {
      const { data } = await api.post("/inventory/documents", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (value) {
    return (
      <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-emerald-200 bg-emerald-50 text-sm" data-testid={testId}>
        <Paperclip size={14} className="text-emerald-700 shrink-0" />
        <a
          href={documentUrl(value.id)}
          target="_blank"
          rel="noreferrer"
          className="flex-1 truncate text-emerald-800 hover:underline"
          title={value.file_name}
        >
          {value.file_name}
        </a>
        <ArrowSquareOut size={12} className="text-emerald-700 shrink-0" />
        <button type="button" onClick={() => onChange(null)} className="p-1 text-slate-500 hover:text-rose-600" title="Remove">
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp"
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0])}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 h-10 px-3 rounded-md border border-dashed border-slate-300 bg-white text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        data-testid={testId}
      >
        <Paperclip size={14} /> {busy ? "Uploading…" : label}
      </button>
    </>
  );
}
